import { css, cx } from "@emotion/css";
import { ClickAwayListener, TextField } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";
import { useFormik } from "formik";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import type { KeyboardEvent, ReactNode, RefObject } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { AiResponseReviewForm } from "src/components/AI/AiResponseReviewForm";
import {
	IconButton,
	OutlinedTextBlackButton,
	PrimaryButton,
} from "src/components/Default/LekUI/Button";
import { ScrollShadowWrapper } from "src/components/Default/LekUI/ScrollShadowWrapper";
import { Tooltip } from "src/components/Default/LekUI/Tooltip";
import StarsIcon from "src/images/Icons/newIcons/aiStar.svg";
import CloseIcon from "src/images/Icons/newIcons/close.svg";
import SendIcon from "src/images/Icons/newIcons/send.svg";
import * as Yup from "yup";
import "src/styles/tiptapEditor.scss";
import { SwipeableContent } from "src/components/Default/LekUI/SwipableDrawer";
import { normalizeAiMarkdown } from "src/helpers/normalizeAiMarkdown";
import { useGetAIResponseRate, useToggleAIResponseRate } from "src/hooks/useAi";
import { useFontSize } from "src/hooks/useFontSize";
import { isStaff } from "src/hooks/useUser";

interface ChatPart {
	type: string;
	text: string;
}

export interface ChatMessage {
	id: string;
	role: "user" | "assistant";
	parts: ChatPart[];
	isLoading?: boolean;
	allowRate?: boolean;
}

interface ChatInputProps {
	onSubmit: (values: { message: string }) => Promise<void>;
	isLoading: boolean;
	disabled: boolean;
	onMessageChange?: (message: string) => void;
	initialMessage?: string;
}

interface HeaderProps {
	onClose: () => void;
	fontSizeClassName?: string;
}

interface RateAnswerProps {
	responseId: number | string;
	likesCount?: number;
	dislikesCount?: number;
}

interface SystemChatProps {
	children: ReactNode;
	isLoading?: boolean;
	allowRate?: boolean;
	responseId: number | string;
}

interface ContentProps {
	chat: ChatMessage[];
	emptyState?: ReactNode;
	bottomAnchor?: RefObject<HTMLDivElement | null>;
}

interface AIChatbotProps {
	onClose: () => void;
	className?: string;
	chat: ChatMessage[];
	footer?: ReactNode;
	bottomAnchor?: RefObject<HTMLDivElement | null>;
	emptyState?: ReactNode;
}

export const ChatInput = ({
	onSubmit,
	isLoading,
	disabled,
	onMessageChange,
	initialMessage,
}: ChatInputProps) => {
	const { values, handleSubmit, setFieldValue } = useFormik({
		initialValues: {
			message: initialMessage || "",
		},
		validateOnChange: true,
		onSubmit: async (values, { resetForm }) => {
			onMessageChange?.("");
			try {
				await onSubmit(values);
				resetForm();
			} catch (err) {
				resetForm();
			}
		},
		validationSchema: Yup.object().shape({
			message: Yup.string().max(2000),
		}),
	});

	return (
		<TextField
			className={cx(
				"text-field",
				css`
          .MuiInputBase-root {
            padding: 12px;
          }
        `,
			)}
			placeholder={
				disabled ? "Limit zapytań został osiągnięty" : "Dowiedz się więcej..."
			}
			fullWidth
			multiline
			maxRows={5}
			value={values.message}
			disabled={isLoading || disabled}
			onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === "Enter") {
					if (e.ctrlKey || e.metaKey) {
						const target = e.target as HTMLTextAreaElement;
						const { selectionStart, selectionEnd } = target;
						const newValue =
							values.message.substring(0, selectionStart) +
							"\n" +
							values.message.substring(selectionEnd);

						setFieldValue("message", newValue);
						Promise.resolve().then(() => {
							target.selectionStart = selectionStart + 1;
							target.selectionEnd = selectionStart + 1;
						});
						return;
					}

					e.preventDefault();
					handleSubmit();
				}
			}}
			onChange={(e) => {
				setFieldValue("message", e.target.value);
				onMessageChange?.(e.target.value);
			}}
			slotProps={{
				input: {
					endAdornment: (
						<InputAdornment position="end">
							<Tooltip title="Wyślij">
								<IconButton
									onClick={() => handleSubmit()}
									loading={isLoading}
									disabled={disabled}
								>
									<SendIcon className="icon-normal icon-black" />
								</IconButton>
							</Tooltip>
						</InputAdornment>
					),
				},
			}}
		/>
	);
};

const Header = ({ onClose, fontSizeClassName }: HeaderProps) => {
	return (
		<div className="ai-tutor--header">
			<div className="flex items-center gap-8">
				<div className="ai-tutor-icon">
					<StarsIcon className="icon-small" />
				</div>
				<p className={cx(fontSizeClassName, "m-0")}>Tutor AI</p>
			</div>
			<IconButton onClick={onClose}>
				<CloseIcon className="icon-normal" />
			</IconButton>
		</div>
	);
};

// Markdown z AI w tych samych stylach co artykuły (tiptap-content); --markdown: czcionka i kolor z otoczenia + odstęp akapitów
export const renderAiText = (text: string) => {
	return (
		<div className="tiptap-content tiptap-content--markdown">
			<ReactMarkdown
				remarkPlugins={[remarkGfm, remarkBreaks]}
				rehypePlugins={[
					rehypeRaw,
					[
						rehypeSanitize,
						{
							...defaultSchema,
							attributes: {
								...defaultSchema.attributes,
								"*": [
									...(defaultSchema.attributes?.["*"] || []),
									"style",
									"className",
								],
								a: [
									...(defaultSchema.attributes?.["a"] || []),
									"target",
									"rel",
								],
							},
						},
					],
				]}
				components={{
					a: ({ children, href, ...props }) => (
						<a href={href} target="_blank" rel="noopener noreferrer" {...props}>
							{children}
						</a>
					),
					table: ({ children }) => (
						<ScrollShadowWrapper>
							<table>{children}</table>
						</ScrollShadowWrapper>
					),
					// jak TiptapContent: minimalna szerokość komórki zamiast ściskania kolumn
					td: ({ children }) => (
						<td>
							<div className="tiptap-cell">{children}</div>
						</td>
					),
					th: ({ children }) => (
						<th>
							<div className="tiptap-cell">{children}</div>
						</th>
					),
				}}
			>
				{normalizeAiMarkdown(text)}
			</ReactMarkdown>
		</div>
	);
};

export const RateAnswer = ({
	responseId,
	likesCount,
	dislikesCount,
}: RateAnswerProps) => {
	const { t } = useTranslation();
	const { isLoading, data: like } = useGetAIResponseRate(responseId);
	const isAdmin = isStaff();
	const { mutateAsync: toggleRate } = useToggleAIResponseRate();
	const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);

	useEffect(() => {
		if (like !== false) {
			setIsReviewFormOpen(false);
		}
	}, [like]);

	const toggleLike = async (nextLike: boolean | null) => {
		await toggleRate({
			id: responseId,
			like: nextLike,
		} as any);
	};

	const handleLikeClick = async () => {
		await toggleLike(like === true ? null : true);
	};

	const handleDislikeClick = async () => {
		const nextValue = like === false ? null : false;
		await toggleLike(nextValue);
		setIsReviewFormOpen(nextValue === false);
	};

	if (isLoading) {
		return null;
	}

	const LikeButton = like === true ? PrimaryButton : OutlinedTextBlackButton;
	const DislikeButton =
		like === false ? PrimaryButton : OutlinedTextBlackButton;

	return (
		<div className="flex flex-col gap-8">
			<span>{t("ai.chatbot.rateQuestion")}</span>
			{isAdmin && (
				<span className="size-12-light text-black">
					{t("ai.chatbot.userRatings", "Oceny userów")}: Like:{" "}
					<span className="font-bold">{likesCount ?? 0}</span>, Dislike:{" "}
					<span className="font-bold">{dislikesCount ?? 0}</span>
				</span>
			)}
			<div className="ai-discussion-rating">
				<LikeButton
					className="size-16-light"
					onClick={handleLikeClick}
					endIcon={<ThumbsUp className="icon-small ml-4" />}
				>
					{t("ai.chatbot.responseGood", "Omówienie poprawne")}
				</LikeButton>
				<DislikeButton
					className="size-16-light"
					onClick={handleDislikeClick}
					endIcon={<ThumbsDown className="icon-small ml-4" />}
				>
					{t("ai.chatbot.responseBad", "Omówienie błędne")}
				</DislikeButton>
			</div>
			{like === false && isReviewFormOpen && (
				<AiResponseReviewForm
					responseId={responseId}
					onSubmitted={() => setIsReviewFormOpen(false)}
					onCancel={() => setIsReviewFormOpen(false)}
				/>
			)}
		</div>
	);
};

const SystemChat = ({
	children,
	isLoading,
	allowRate,
	responseId,
}: SystemChatProps) => {
	const { fontSizeClass, fontSizeDeepClass } = useFontSize();
	const { t } = useTranslation();

	return (
		<>
			<div className="ai-tutor--chat ai-tutor--chat--system">
				<div className="flex items-center gap-8">
					<div className="ai-tutor-icon">
						<StarsIcon className="icon-small" />
					</div>
					<span className={fontSizeClass}>{t("ai.chatbot.header")}</span>
				</div>
				{isLoading ? (
					<p className="ai-tutor--generate--loader">
						{t("ai.chatbot.loader")}
						<span>.</span>
						<span>.</span>
						<span>.</span>
					</p>
				) : null}
				{!isLoading && (
					<div className={cx(fontSizeClass, fontSizeDeepClass)}>{children}</div>
				)}
			</div>
			{allowRate && !isLoading && <RateAnswer responseId={responseId} />}
		</>
	);
};

const Content = ({ chat, emptyState, bottomAnchor }: ContentProps) => {
	const { t } = useTranslation();

	if (chat.length === 0) {
		return emptyState;
	}

	return (
		<div className="ai-tutor--content">
			{chat.map(({ role, parts, isLoading, allowRate, id }) => {
				if (role === "user") {
					return (
						<div key={id} className="ai-tutor--chat ai-tutor--chat--user">
							{parts.map((part) =>
								part.type === "text" ? renderAiText(part.text) : null,
							)}
						</div>
					);
				}

				if (role === "assistant") {
					return (
						<SystemChat
							key={`system-${id}`}
							isLoading={isLoading}
							allowRate={allowRate}
							responseId={id}
						>
							{parts.map((part) =>
								part.type === "text" ? renderAiText(part.text) : null,
							)}
						</SystemChat>
					);
				}

				return null;
			})}
			<span className="text-center text-grey size-12-light">
				{t("ai.chatbot.aiInfo")}
			</span>
			<div ref={bottomAnchor} />
		</div>
	);
};

function AIChatbot({
	onClose,
	className,
	chat,
	footer = null,
	bottomAnchor = null,
	emptyState = null,
}: AIChatbotProps) {
	return (
		<SwipeableContent isOpen onClose={onClose}>
			<ClickAwayListener onClickAway={onClose}>
				<div className={cx("ai-tutor-chatbot-container", className)}>
					<Header onClose={onClose} />
					<Content
						chat={chat}
						emptyState={emptyState}
						bottomAnchor={bottomAnchor}
					/>
					{footer}
				</div>
			</ClickAwayListener>
		</SwipeableContent>
	);
}

export default AIChatbot;
